import React, { useState, type ReactNode } from "react";
import {
  AspectRatio,
  Flex,
  Accordion as ChakraAccordion,
  type AccordionProps as ChakraAccordionProps,
  type SpaceProps,
} from "@chakra-ui/react";
import { remCalc } from "theme/utils";
import { AnimatePresence } from "framer-motion";
import { type Theme } from "contexts/color-theme";
import { ChakraBox } from "ui/chakra-framer";
import { AccordionItem, type AccordionItemProps } from "./accordion-item";

type BaseMediaAccordionProps = {
  /**
   * The variant determines whether or not the media is static or dynamic.
   * If static, the media will not toggle with the accordion.
   */
  variant: "static" | "dynamic";

  /**
   * The size of the media
   *
   * @default "sm"
   */
  size?: "sm" | "lg";

  /**
   * The direction of the media
   *
   * @default "left"
   */
  direction?: "left" | "right";

  /**
   * The max-width of the accordion items.
   */
  itemsMaxWidth?: ChakraAccordionProps["maxW"];

  /**
   * The color theme of the accordion.
   *
   * @default "light"
   */
  theme?: Theme;
};

type MediaItemProps = AccordionItemProps & {
  /**
   * The media to render in the accordion item. Each item can have different media.
   * Can be either an image or lottie animation
   */
  media: ReactNode;
};

type DynamicVariantProps = BaseMediaAccordionProps & {
  variant: "dynamic";
  items: MediaItemProps[];
};

type StaticVariantProps = BaseMediaAccordionProps & {
  variant?: "static";
  items: AccordionItemProps[];
  /**
   * Static media to display in the accordion. Does not toggle with the accordion.
   * Can be either an image or lottie animation.
   */
  media: ReactNode;
};

export type MediaAccordionProps = (StaticVariantProps | DynamicVariantProps) & SpaceProps;

export function MediaAccordion({
  items,
  itemsMaxWidth,
  variant,
  size = "sm",
  direction = "left",
  theme = "light",
  ...props
}: MediaAccordionProps) {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const itemsMaxW = itemsMaxWidth ?? { base: "100%", md: size === "sm" ? remCalc(425) : remCalc(324) };
  const staticMedia = variant === "static" && (props as StaticVariantProps).media;
  const activeMedia = variant === "dynamic" && items[expandedIndex]?.media;

  return (
    <ChakraAccordion
      // Only static variants can support toggling.
      // Dynamic variants require a media element for each active index, and can never be null
      allowToggle={variant === "static"}
      defaultIndex={0}
      display="flex"
      alignItems="center"
      flexDir={{ base: "column", md: direction === "right" ? "row-reverse" : "row" }}
      gap={{ base: "smallest", md: "biggest" }}
      justifyContent={{ base: "center", md: "space-between" }}
      onChange={(index) => {
        // Since we do not support `allowMultiple`, we can safely cast the index to a number
        setExpandedIndex(index as number);
      }}
      {...props}
    >
      <AspectRatio
        borderRadius={{ base: "small", md: "bigger" }}
        ratio={size === "sm" ? 1 / 1 : 872 / 648}
        flex={1}
        w={{ base: "100%", md: size === "sm" ? "50%" : "66%" }}
        maxW={{ base: "mediaMaxW.mobile", md: "100%", lg: size === "sm" ? "648px" : "872px" }}
        pos="relative"
        overflow="hidden"
      >
        {variant === "static" ? (
          staticMedia
        ) : (
          <AnimatePresence initial={false}>
            {activeMedia && (
              <ChakraBox
                key={items[expandedIndex].title}
                boxSize="100%"
                pos="relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.25 } }}
                exit={{ opacity: 0, transition: { duration: 0.25 } }}
              >
                {activeMedia}
              </ChakraBox>
            )}
          </AnimatePresence>
        )}
      </AspectRatio>

      <Flex direction="column" flex={1} w={itemsMaxW} maxW={itemsMaxW} mx="auto">
        {items.map((item, index) => (
          <AccordionItem key={item.id ?? item.title} isFirst={index === 0} theme={theme} {...item} />
        ))}
      </Flex>
    </ChakraAccordion>
  );
}
